import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Image, Video, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../Apis/apiConfig";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";

export default function UploadManager() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newText, setNewText] = useState("");
  const [newColor, setColor] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expandedText, setExpandedText] = useState({});
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const navigate = useNavigate(); 

  const token = localStorage.getItem("token");
  const societyName = localStorage.getItem("society");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/welcome/get-welcome-by-admin?societyName=${societyName}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await fetch(
        `${BASE_URL}/welcome/delete-welcome?welcomeId=${id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setNewText(item.welcomeText);
    setColor(item.welcomeTextColor);
  };

const handleUpdate = async () => {
  try {
    const formData = new FormData();
    formData.append("welcomeId", editItem.welcomeId);
    formData.append("welcomeText", newText);
    formData.append("textColor", newColor);
    if (editItem.newImage) {
      formData.append("backgroundImage", editItem.newImage);
    }
    if (editItem.newVideo) {
      formData.append("backgroundVideo", editItem.newVideo);
    }
    await fetch(`${BASE_URL}/welcome/edit-by-admin`, {
      method: "POST", 
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    setEditItem(null);
    fetchData();
  } catch (err) {
    console.error(err);
  }
};

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />
          <div className="p-6 bg-blue-50 min-h-screen">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-1 mb-3 px-2 py-1 text-xs rounded-md 
            bg-blue-100 text-blue-600 hover:bg-blue-200 
            transition-all duration-150"
          >
          <ArrowLeft size={14} />
          Back
        </button>

            <div className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-100 text-blue-700">
                    <tr>
                      <th className="p-3 text-left">Text</th>
                      <th className="p-3">Image</th>
                      <th className="p-3">Video</th>
                        <th className="p-3">Text Color</th>
                      <th className="p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center p-6 text-blue-500">
                          Loading...
                        </td>
                      </tr>
                    ) : data.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center p-6 text-blue-500">
                          No Data Found
                        </td>
                      </tr>
                    ) : (
                      data.map((item) => (
                        <tr key={item.welcomeId} className="border-t border-blue-100 hover:bg-blue-50 transition" >
                          <td className="p-3 text-gray-700 font-medium max-w-[250px]">
                            {expandedText[item.welcomeId]
                              ? item.welcomeText
                              : item.welcomeText?.slice(0, 50)}

                            {item.welcomeText?.length > 50 && (
                              <button
                                onClick={() =>
                                  setExpandedText((prev) => ({
                                    ...prev,
                                    [item.welcomeId]: !prev[item.welcomeId],
                                  }))
                                }
                                className="ml-2 text-blue-600 text-xs font-semibold"
                              >
                                {expandedText[item.welcomeId]
                                  ? "See Less"
                                  : "See More"}
                              </button>
                            )}
                          </td>
                        
                          <td className="p-3 text-center">
                            {item.backgroundImage ? (
                              <img
                                src={`data:image/jpeg;base64,${item.backgroundImage}`}
                                onClick={() =>
                                  setPreview({
                                    type: "image",
                                    src: `data:image/jpeg;base64,${item.backgroundImage}`,
                                  })
                                }
                                className="h-14 w-20 mx-auto rounded object-cover border border-blue-200 shadow-sm cursor-pointer hover:scale-105 transition"
                              />
                            ) : (
                              <Image size={18} className="mx-auto text-blue-300" />
                            )}
                          </td>

                          <td className="p-3 text-center">
                            {item.backgroundVideo ? (
                              <video
                                src={`data:video/mp4;base64,${item.backgroundVideo}`}
                                onClick={() =>
                                  setPreview({
                                    type: "video",
                                    src: `data:video/mp4;base64,${item.backgroundVideo}`,
                                  })
                                }
                                className="h-14 w-24 mx-auto rounded border border-blue-200 shadow-sm cursor-pointer hover:scale-105 transition"
                              />
                            ) : (
                              <Video size={18} className="mx-auto text-blue-300" />
                            )}
                          </td>
                          <td className="p-3 text-center">
                           {item.welcomeTextColor}
                          </td>

                          <td className="p-3">
                            <div className="flex justify-center gap-3">
                              <button
                                onClick={() => handleEdit(item)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 shadow-sm transition text-sm font-medium"
                              >
                                <Pencil size={14} />
                                Edit
                              </button>

                              <button
                                onClick={() => handleDelete(item.welcomeId)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 shadow-sm transition text-sm font-medium"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

              </div>
            </div>
            {editItem && (
              <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="relative bg-white w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl border border-blue-100 p-4 sm:p-6">
                  
                  <button
                    onClick={() => setEditItem(null)}
                    className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-lg"
                  >
                    ✕
                  </button>

                  <h2 className="text-lg sm:text-xl font-semibold mb-4 text-blue-700">
                    Edit Content
                  </h2>

                  <textarea
                    className="w-full border border-blue-200 p-3 rounded-lg focus:ring-2 focus:ring-blue-400 outline-none mb-4 text-sm sm:text-base min-h-[120px] sm:min-h-[140px] md:min-h-[160px]"
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Edit your content..."
                  />
                <h3 className="text-sm font-medium text-blue-600 mb-1">
              Text Color
            </h3>

            <div className="flex gap-2 mb-4">
              <input
                type="color"
                value={newColor || "#0000ff"}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-16 border rounded cursor-pointer"
              />

              {/* Text Input */}
              <input
                type="text"
                value={newColor}
                onChange={(e) => setColor(e.target.value)}
                placeholder="Enter color (e.g. blue or #0000ff)"
                className="flex-1 p-2 border rounded"
              />
            </div>
                  <div className="mb-4">
                    <p className="text-xs sm:text-sm text-blue-600 mb-2">Image Preview / Upload</p>
                    {editItem.backgroundImage && (
                      <img
                        src={`data:image/jpeg;base64,${editItem.backgroundImage}`}
                        className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover rounded-lg border border-blue-200 mb-2"
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditItem((prev) => ({ ...prev, newImage: e.target.files?.[0] }))
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="mb-4">
                    <p className="text-xs sm:text-sm text-blue-600 mb-2">Video Preview / Upload</p>
                    {editItem.backgroundVideo && (
                      <video
                        src={`data:video/mp4;base64,${editItem.backgroundVideo}`}
                        controls
                        className="w-full h-40 sm:h-48 md:h-56 lg:h-64 rounded-lg border border-blue-200 object-cover mb-2"
                      />
                    )}
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) =>
                        setEditItem((prev) => ({ ...prev, newVideo: e.target.files?.[0] }))
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                    <button
                      onClick={() => setEditItem(null)}
                      className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleUpdate}
                      className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      Update
                    </button>
                  </div>

                </div>
              </div>
            )}
            {preview && (
              <div
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
                onClick={() => setPreview(null)}
              >
                <div className="bg-white p-4 rounded-xl max-w-3xl w-full">
                  {preview.type === "image" ? (
                    <img
                      src={preview.src}
                      className="w-full max-h-[500px] object-contain rounded"
                    />
                  ) : (
                    <video
                      src={preview.src}
                      controls
                      className="w-full max-h-[500px] rounded"
                    />
                  )}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}